// ═══════════════════════════════════════════════════════════════
// ArchiveModal.tsx – Archívum (szervezetenként)
//
// - A jogszabály szerint megőrzendő tételek (oldal, bejegyzés, melléklet, automatikusan archivált korábbi
//   állapot) listája a megőrzési idővel; az összegzés a leghosszabb kötelező megőrzést mutatja.
// - Visszaállítás: aki törölhet. Közzététel kérésre, teljes letöltés ZIP-ben, visszatöltés: admin.
// - Végleges törlés: a megőrzési idő lejárta után az admin; előtte csak superadmin, írásos indoklással.
// - Superadmin: automatikus archiválás be/ki (ennél a szervezetnél / minden szervezetnél).
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ApiClient, type ArchiveItem, type ArchiveSummary } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { fmtDay, retentionLabel } from './ArchiveDialog';
import {
  X, Archive, RotateCcw, RefreshCw, FileText, Paperclip, LayoutList, Search, AlertTriangle, Download, Upload,
  Globe, Lock, Unlock, Trash2, History, ShieldCheck, Settings2,
} from 'lucide-react';

type Kind = ArchiveItem['kind'];
const KIND: Record<Kind, { label: string; Icon: React.FC<{ className?: string }> }> = {
  page: { label: 'Oldal', Icon: LayoutList },
  record: { label: 'Bejegyzés', Icon: FileText },
  attachment: { label: 'Melléklet', Icon: Paperclip },
  version: { label: 'Korábbi állapot', Icon: History },
};
const fmtDate = (s: string) => { const d = new Date(s.replace(' ', 'T') + 'Z'); return isNaN(+d) ? s : d.toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' }); };
const fmtSize = (b: number) => (b <= 0 ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

export const ArchiveModal: React.FC<{ isOpen: boolean; onClose: () => void; onRestored?: (r: { page_id?: number; record_id?: number }) => void }> = ({ isOpen, onClose, onRestored }) => {
  const { role, tenant } = useAuth();
  const isSuper = String(role) === 'superadmin';
  const isAdmin = ['superadmin', 'tenant_admin', 'org_admin'].includes(String(role));
  const [items, setItems] = useState<ArchiveItem[]>([]);
  const [summary, setSummary] = useState<ArchiveSummary | null>(null);
  const [auto, setAuto] = useState(false);
  const [today, setToday] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | 'zip' | 'import' | 'auto' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [kind, setKind] = useState<'all' | Kind>('all');
  const [q, setQ] = useState('');
  const [confirmZip, setConfirmZip] = useState(false);
  const [delItem, setDelItem] = useState<ArchiveItem | null>(null);
  const [reason, setReason] = useState('');
  const [autoCfg, setAutoCfg] = useState<{ all: boolean; tenants: number[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // a ZIP jelszava: a szervezet egyedi belépési linkjének azonosítója (adminnak megjeleníthető)
  const [zipPw, setZipPw] = useState<string | null>(null);
  const [showPw, setShowPw] = useState(false);
  const [copied, setCopied] = useState(false);
  // visszatöltés régi jelszóval (ha azóta új belépési linket generáltak)
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [importPw, setImportPw] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    const r = await ApiClient.getArchive();
    if (r.ok) { setItems(r.data || []); setSummary(r.summary || null); setAuto(Boolean(r.auto)); if (r.today) setToday(r.today); } else setError(r.error || 'Az archívum nem tölthető be');
    setLoading(false);
    if (isSuper) { const c = await ApiClient.getArchiveAuto(); if (c.ok && c.data) setAutoCfg(c.data); }
  };
  useEffect(() => { if (isOpen) { setNotice(null); setConfirmZip(false); setDelItem(null); load(); } }, [isOpen]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((it) => (kind === 'all' || it.kind === kind) && (!s || `${it.title} ${it.page_title || ''} ${it.pont || ''} ${it.note || ''}`.toLowerCase().includes(s)));
  }, [items, kind, q]);
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: items.length };
    for (const it of items) c[it.kind] = (c[it.kind] || 0) + 1;
    return c;
  }, [items]);

  if (!isOpen) return null;

  const locked = (it: ArchiveItem) => Boolean(it.retain_until && it.retain_until > today);

  const restore = async (it: ArchiveItem) => {
    if (it.kind === 'version' && !window.confirm('A bejegyzés jelenlegi tartalma helyére ez a korábbi állapot kerül vissza. A korábbi állapot az Archívumban is megmarad. Folytatod?')) return;
    setBusy(it.id); setError(null); setNotice(null);
    const r = await ApiClient.restoreArchiveItem(it.id);
    if (r.ok) {
      setNotice(r.kept
        ? `Visszaírva: „${it.title}”${r.as_new ? ' (új bejegyzésként, mert az eredeti már nem létezik)' : ''}. A korábbi állapot az Archívumban maradt.`
        : `Visszaállítva: ${KIND[it.kind].label.toLowerCase()} – „${it.title}”${r.renamed ? ` (az oldal azonosítója foglalt volt, új azonosító: ${r.renamed})` : ''}.`);
      onRestored?.(r);
      load();
    } else setError(r.error || 'A visszaállítás nem sikerült');
    setBusy(null);
  };

  const togglePublic = async (it: ArchiveItem) => {
    const pub = !it.is_public;
    if (pub && !window.confirm(`Közzéteszed a nyilvános portálon (Archívum rész): „${it.title}”?\nA tétel belső mezői (frissítés, megőrzés) nem jelennek meg.`)) return;
    setBusy(it.id); setError(null);
    const r = await ApiClient.publishArchiveItem(it.id, pub);
    if (r.ok) setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, is_public: pub ? 1 : 0, published_at: pub ? new Date().toISOString().slice(0, 19).replace('T', ' ') : null } : x)));
    else setError(r.error || 'Nem sikerült');
    setBusy(null);
  };

  const askDelete = (it: ArchiveItem) => {
    if (!locked(it)) {
      if (!window.confirm(`Véglegesen törlöd az Archívumból: „${it.title}”? A tétel és a fájljai törlődnek, ez nem vonható vissza.`)) return;
      doDelete(it, '');
      return;
    }
    setReason(''); setDelItem(it);
  };
  const doDelete = async (it: ArchiveItem, why: string) => {
    setBusy(it.id); setError(null); setNotice(null);
    const r = await ApiClient.deleteArchiveItem(it.id, why);
    if (r.ok) { setDelItem(null); setNotice(`Véglegesen törölve: „${it.title}”${r.early ? ' (a megőrzési idő lejárta előtt – az indoklás a műveleti naplóba került)' : ''}.`); load(); }
    else setError(r.error || 'A törlés nem sikerült');
    setBusy(null);
  };

  const openZipConfirm = async () => {
    const next = !confirmZip;
    setConfirmZip(next); setShowPw(false); setCopied(false);
    if (next && zipPw === null && tenant?.id) {
      const r = await ApiClient.getTenantLoginLink(Number(tenant.id)).catch(() => null);
      setZipPw(r?.ok && r.data?.token ? r.data.token : '');
    }
  };
  const copyPw = async () => {
    if (!zipPw) return;
    try { await navigator.clipboard.writeText(zipPw); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { setShowPw(true); }
  };
  const download = async () => {
    setBusy('zip'); setError(null);
    const r = await ApiClient.downloadArchiveZip();
    if (!r.ok) setError(r.error || 'A letöltés nem sikerült'); else { setConfirmZip(false); setNotice(`Az archívum letöltése elindult (AES-256 titkosítással${r.fingerprint ? `, azonosító-ujjlenyomat: ${r.fingerprint}` : ''}).`); }
    setBusy(null);
  };
  const upload = async (f: File | undefined | null, password?: string) => {
    if (!f) return;
    setBusy('import'); setError(null); setNotice(null);
    const r = await ApiClient.importArchiveZip(f, undefined, password);
    if (r.ok) {
      setPendingFile(null); setImportPw('');
      setNotice(`Visszatöltve: ${r.imported ?? 0} tétel${r.files ? `, ${r.files} fájl` : ''}${r.skipped ? ` · ${r.skipped} már szerepelt (kihagyva)` : ''}${r.missing_files?.length ? ` · ${r.missing_files.length} fájl hiányzott a csomagból` : ''}.`);
      load();
    } else {
      if (r.code === 'NEED_PASSWORD' || r.code === 'BAD_PASSWORD') setPendingFile(f);
      setError(r.error || 'A visszatöltés nem sikerült');
    }
    if (fileRef.current) fileRef.current.value = '';
    setBusy(null);
  };

  const saveAuto = async (next: { all: boolean; tenants: number[] }) => {
    setBusy('auto'); setError(null);
    const r = await ApiClient.setArchiveAuto(next);
    if (r.ok && r.data) { setAutoCfg(r.data); setAuto(r.data.all || (tenant?.id ? r.data.tenants.includes(Number(tenant.id)) : false)); }
    else setError(r.error || 'A beállítás nem menthető');
    setBusy(null);
  };
  const tid = Number(tenant?.id) || 0;

  const max = summary?.max || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs" role="dialog" aria-modal="true" aria-labelledby="archive-title"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); if (delItem) setDelItem(null); else onClose(); } }}
      onClick={(e) => { if (e.target === e.currentTarget && busy === null) onClose(); }}>
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-5xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div>
            <h2 id="archive-title" className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2"><Archive className="w-5 h-5 text-amber-600" /> Archívum</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              A jogszabály szerint megőrzendő tételek belső tárolója. A megőrzési idő lejárta előtt csak superadmin törölhet, írásos indoklással. Kérésre tételenként közzétehető. Az archivált fájlok a tárhelykvótába beleszámítanak.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Bezárás" className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"><X className="w-5 h-5" /></button>
        </div>

        {/* Összegzés: leghosszabb kötelező megőrzés */}
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-x-6 gap-y-2 bg-amber-50/60 dark:bg-amber-950/20">
          <div className="flex items-start gap-2 min-w-0">
            <ShieldCheck className="w-5 h-5 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800 dark:text-amber-300">Leghosszabb kötelező megőrzés az archívumban</div>
              {max ? (
                <div className="text-sm text-slate-900 dark:text-white">
                  <b>{fmtDay(max.retain_until)}-ig</b> · {retentionLabel({ years: max.years, min: max.min, basis: max.basis })}
                  <span className="text-xs text-slate-600 dark:text-slate-400 break-words"> – {max.pont && !String(max.title || '').startsWith(max.pont) ? `${max.pont} · ` : ''}{max.title}</span>
                </div>
              ) : <div className="text-sm text-slate-600 dark:text-slate-400">Nincs megőrzési idővel jelölt tétel.</div>}
            </div>
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-400 flex flex-wrap gap-x-3">
            <span>{summary?.count ?? 0} tétel</span>
            {summary && summary.locked > 0 && <span className="text-amber-700 dark:text-amber-400 font-semibold">{summary.locked} megőrzési idő alatt</span>}
            {summary && fmtSize(summary.bytes) && <span>{fmtSize(summary.bytes)}</span>}
            <span className={auto ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : ''}>Automatikus archiválás: {auto ? 'be' : 'ki'}</span>
          </div>
        </div>

        {isSuper && autoCfg && (
          <div className="px-5 py-2.5 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-700 dark:text-slate-300">
            <span className="font-semibold flex items-center gap-1.5"><Settings2 className="w-3.5 h-3.5 text-indigo-500" /> Automatikus archiválás (superadmin)</span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer" title="Bejegyzés módosításakor az előző állapot az Archívumba kerül">
              <input type="checkbox" disabled={busy === 'auto' || autoCfg.all || !tid} checked={autoCfg.all || autoCfg.tenants.includes(tid)}
                onChange={(e) => saveAuto({ all: autoCfg.all, tenants: e.target.checked ? [...autoCfg.tenants, tid] : autoCfg.tenants.filter((x) => x !== tid) })} />
              ennél a szervezetnél{tenant?.name ? ` (${tenant.name})` : ''}
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" disabled={busy === 'auto'} checked={autoCfg.all}
                onChange={(e) => { if (!e.target.checked || window.confirm('Minden szervezetnél bekapcsolod? Ezután minden bejegyzés-módosításkor az előző állapot az Archívumba kerül.')) saveAuto({ all: e.target.checked, tenants: autoCfg.tenants }); }} />
              minden szervezetnél
            </label>
            {!autoCfg.all && autoCfg.tenants.length > 0 && <span className="text-slate-500">{autoCfg.tenants.length} szervezetnél bekapcsolva</span>}
          </div>
        )}

        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto" role="group" aria-label="Típus">
            {(['all', 'page', 'record', 'attachment', 'version'] as const).map((k) => (
              <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap ${kind === k ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs' : 'text-slate-500'}`}>
                {k === 'all' ? 'Összes' : KIND[k].label} <span className="ml-0.5 text-[10px] opacity-70">{counts[k] || 0}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[10rem]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés…" aria-label="Keresés az archívumban"
              className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white" />
          </div>
          <button type="button" onClick={load} title="Frissítés" aria-label="Frissítés" className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {isAdmin && (
            <>
              <button type="button" onClick={openZipConfirm} disabled={busy !== null || !items.length}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer disabled:opacity-40">
                <Download className="w-3.5 h-3.5" /> Teljes letöltés (ZIP)
              </button>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy !== null}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer disabled:opacity-40">
                {busy === 'import' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Visszatöltés ZIP-ből
              </button>
              <input ref={fileRef} type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => upload(e.target.files?.[0])} aria-label="Archívum ZIP kiválasztása" />
            </>
          )}
        </div>

        {confirmZip && (
          <div className="mx-5 mt-3 p-3 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/30 text-xs text-slate-700 dark:text-slate-300 flex flex-wrap items-center gap-3" role="region" aria-label="Letöltés megerősítése">
            <div className="flex-1 min-w-[14rem]">
              <b>A teljes archívum ({summary?.count ?? 0} tétel{summary && fmtSize(summary.bytes) ? `, ${fmtSize(summary.bytes)}` : ''}) letöltése.</b>{' '}
              {max
                ? <>A csomagban lévő leghosszabb kötelező megőrzés: <b>{retentionLabel({ years: max.years, min: max.min, basis: max.basis })} – {fmtDay(max.retain_until)}-ig</b> ({max.pont ? `${max.pont} pont, ` : ''}„{max.text}”). A letöltött csomagot legalább eddig meg kell őrizni.</>
                : <>A csomagban nincs megőrzési idővel jelölt tétel.</>}
              {' '}Ugyanennek a szervezetnek az Archívumába visszatölthető.
              <div className="mt-2 p-2 rounded-lg bg-white/70 dark:bg-slate-900/60 border border-indigo-200 dark:border-indigo-800 space-y-1">
                <div><b>AES-256 jelszóval védett csomag.</b> A jelszó a szervezet egyedi belépési linkjének azonosítója (a link <code>/login/</code> utáni része). Megnyitás: 7-Zip, WinRAR vagy macOS Archive Utility – a Windows beépített kicsomagolója nem kezeli.</div>
                {zipPw ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span>Jelszó:</span>
                    <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono select-all break-all">{showPw ? zipPw : '•'.repeat(Math.min(24, zipPw.length))}</code>
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="underline cursor-pointer">{showPw ? 'Elrejtés' : 'Megjelenítés'}</button>
                    <button type="button" onClick={copyPw} className="underline cursor-pointer">{copied ? 'Másolva ✓' : 'Másolás'}</button>
                  </div>
                ) : zipPw === '' ? <div className="text-slate-500">A jelszó (belépési link) a felhasználókezelési joggal rendelkező adminnak látható.</div> : null}
                <div className="text-slate-500">Ha később új belépési linket generálnak, a már letöltött csomagok jelszava a régi azonosító marad – őrizze meg a csomaggal együtt.</div>
              </div>
            </div>
            <button type="button" onClick={download} disabled={busy !== null}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl font-semibold bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer disabled:opacity-40">
              {busy === 'zip' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Letöltés
            </button>
            <button type="button" onClick={() => setConfirmZip(false)} className="px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 cursor-pointer">Mégse</button>
          </div>
        )}

        <div className="flex-1 overflow-auto p-5 space-y-2">
          {error && <p role="alert" className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />{error}</p>}
          {pendingFile && (
            <div className="p-3 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-xs flex flex-wrap items-center gap-2" role="region" aria-label="Visszatöltés jelszóval">
              <span className="flex-1 min-w-[12rem]">„{pendingFile.name}” – add meg a csomag letöltéskori jelszavát (a szervezet akkori belépési linkjének azonosítóját):</span>
              <input type="password" value={importPw} onChange={(e) => setImportPw(e.target.value)} autoFocus aria-label="A csomag jelszava"
                className="px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-950 border border-amber-300 dark:border-amber-800 text-slate-900 dark:text-white" />
              <button type="button" onClick={() => upload(pendingFile, importPw.trim())} disabled={busy !== null || !importPw.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold bg-amber-600 hover:bg-amber-500 text-white cursor-pointer disabled:opacity-50">
                <Upload className="w-3.5 h-3.5" /> Visszatöltés
              </button>
              <button type="button" onClick={() => { setPendingFile(null); setImportPw(''); setError(null); }} className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 cursor-pointer">Mégse</button>
            </div>
          )}
          {notice && <p role="status" className="text-xs text-emerald-700 dark:text-emerald-400">{notice}</p>}
          {!loading && !visible.length && <p className="py-12 text-center text-sm text-slate-500">{items.length ? 'Nincs találat.' : 'Az archívum üres. Archiválni a bejegyzések és az oldalak „Archiválás” műveletével lehet.'}</p>}
          {visible.map((it) => {
            const { Icon, label } = KIND[it.kind] || KIND.record;
            let detail: { records?: number; attachments?: number; record?: string } = {};
            try { detail = it.detail ? JSON.parse(it.detail) : {}; } catch { detail = {}; }
            const isLocked = locked(it);
            const canDelete = isAdmin && (!isLocked || isSuper);
            return (
              <div key={it.id} className="p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                <div className="flex flex-wrap sm:flex-nowrap items-start gap-3">
                  <Icon className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white break-words flex flex-wrap items-center gap-2">
                      <span>{it.title || '(cím nélkül)'}</span>
                      {it.is_public ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-100 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300"><Globe className="w-3 h-3" /> Közzétéve</span> : null}
                      {it.source === 'auto' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">automatikus</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 flex flex-wrap gap-x-2">
                      <span className="font-semibold">{label}</span>
                      {it.kind === 'page' && <span>{detail.records ?? 0} bejegyzés · {detail.attachments ?? 0} melléklet</span>}
                      {it.kind !== 'page' && it.page_title && <span>Oldal: {it.page_title}{!it.page_exists ? ' (már nem létezik)' : ''}</span>}
                      {it.kind === 'attachment' && detail.record && <span>Bejegyzés: {detail.record}</span>}
                      {fmtSize(it.size_bytes) && <span>{fmtSize(it.size_bytes)}</span>}
                      <span>Archiválva: {fmtDate(it.archived_at)}{it.archived_by_name ? ` · ${it.archived_by_name}` : ''}</span>
                    </div>
                    <div className={`mt-1 text-[11px] flex flex-wrap items-center gap-x-2 ${isLocked ? 'text-amber-800 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-400'}`}>
                      {isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                      {it.retain_until
                        ? <span><b>Megőrzés: {fmtDay(it.retain_until)}-ig</b> · {retentionLabel({ years: it.retention_years, min: it.retention_min, basis: it.retention_basis })}{isLocked ? '' : ' · lejárt, törölhető'}</span>
                        : <span>Megőrzési idő: {it.retention_years === 0 ? 'nincs előírva' : 'nincs megadva'} – törölhető</span>}
                      {it.retention_text && <span className="text-slate-500 dark:text-slate-400">({it.pont ? `${it.pont}: ` : ''}„{it.retention_text}”)</span>}
                      {Boolean(it.retention_min) && <span className="font-semibold">· külön jogszabály is előírhat hosszabbat!</span>}
                    </div>
                    {it.note && <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-300 break-words">Megjegyzés: {it.note}</div>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button type="button" onClick={() => restore(it)} disabled={busy !== null}
                      title={it.kind === 'version' ? 'A bejegyzés visszaírása erre a korábbi állapotra (a változat az Archívumban marad)' : 'Visszaállítás az eredeti helyére'}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer disabled:opacity-40">
                      {busy === it.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} {it.kind === 'version' ? 'Visszaírás' : 'Visszaállítás'}
                    </button>
                    {isAdmin && (
                      <button type="button" onClick={() => togglePublic(it)} disabled={busy !== null} aria-pressed={Boolean(it.is_public)}
                        title={it.is_public ? 'Közzététel visszavonása' : 'Közzététel kérésre a nyilvános portál Archívum részében'}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer disabled:opacity-40 ${it.is_public ? 'border-sky-300 dark:border-sky-800 text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40' : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                        <Globe className="w-3.5 h-3.5" /> {it.is_public ? 'Visszavonás' : 'Közzététel'}
                      </button>
                    )}
                    {canDelete && (
                      <button type="button" onClick={() => askDelete(it)} disabled={busy !== null} aria-label={`${it.title} végleges törlése`}
                        title={isLocked ? 'Törlés a megőrzési idő lejárta előtt (superadmin, indoklással)' : 'Végleges törlés'}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer disabled:opacity-40">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                {delItem?.id === it.id && (
                  <div className="mt-3 p-3 rounded-xl border border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 space-y-2" role="region" aria-label="Törlés indoklása">
                    <p className="text-xs text-rose-800 dark:text-rose-300 flex items-start gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0" />
                      A tétel kötelező megőrzési ideje <b>&nbsp;{fmtDay(it.retain_until)}-ig&nbsp;</b> tart. Lejárat előtti végleges törléshez írásos indoklás kötelező; az indoklás a műveleti naplóba kerül.</p>
                    <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} autoFocus aria-label="A törlés indoklása"
                      placeholder="pl. tévesen archivált tétel; jogerős határozat száma…" className="w-full px-3 py-2 bg-white dark:bg-slate-950 border border-rose-300 dark:border-rose-800 rounded-lg text-sm text-slate-900 dark:text-white" />
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-[11px] text-slate-500 mr-auto">{reason.trim().length < 10 ? `még legalább ${10 - reason.trim().length} karakter` : ''}</span>
                      <button type="button" onClick={() => setDelItem(null)} className="px-3 py-1.5 rounded-lg text-xs border border-slate-300 dark:border-slate-700 cursor-pointer">Mégse</button>
                      <button type="button" onClick={() => doDelete(it, reason.trim())} disabled={busy !== null || reason.trim().length < 10}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-600 hover:bg-rose-500 text-white cursor-pointer disabled:opacity-40">
                        <Trash2 className="w-3.5 h-3.5" /> Végleges törlés indoklással
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ArchiveModal;
